import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useVersion } from '../stores/EnvironmentStore';
import { removePlmPrefix } from './PgmUtilities';

const CONSTANTS = {
    DEFAULT_HELP_PAGE: 'nGeniusPulse',
    HELP_URL: 'https://downloads.netscout.com/nGeniusPulse',
    FALLBACK_VERSION: 'v39',
    URL_MAPPINGS: [
        {
            uiPageRegExp: /^\/sites-overview$/,
            helpPage: 'SitesOverview',
        },
        {
            uiPageRegExp: /^\/(sites|sites-detail|site-detail)$/,
            helpPage: 'Sites1',
        },
        {
            uiPageRegExp: /^\/business-services-overview$/,
            helpPage: 'BusinessServicesOverview',
        },
        {
            uiPageRegExp: /^\/(business-services|business-service-detail)$/,
            helpPage: 'BusinessServices1',
        },
        {
            uiPageRegExp: /^\/alerts$/,
            helpPage: 'Alerts',
        },
        {
            uiPageRegExp: /^\/(servers|server-detail)$/,
            helpPage: 'Servers',
        },
        {
            uiPageRegExp: /^\/(network-devices|device-detail)$/,
            helpPage: 'NetworkDevices',
        },
        {
            uiPageRegExp: /^\/(interfaces|interface-detail)$/,
            helpPage: 'Interfaces',
        },
        {
            uiPageRegExp: /^\/(agents|agent-detail)$/,
            helpPage: 'nPoints1',
        },
        {
            uiPageRegExp: /^\/(tests|test-detail|test-log)$/,
            helpPage: 'ServiceTests1',
        },
        {
            uiPageRegExp: /^\/(custom-dashboard|new-dashboard)$/,
            helpPage: 'MyDashboards',
        },
        {
            uiPageRegExp: /^\/ssids$/,
            helpPage: 'SSIDs',
        },
        {
            uiPageRegExp: /^\/(wireless-controllers|wireless-controller-detail)$/,
            helpPage: 'WirelessControllers',
        },
        {
            uiPageRegExp: /^\/(access-points|access-point-detail)$/,
            helpPage: 'AccessPoints',
        },
        {
            uiPageRegExp: /^\/(radios|radio-detail)$/,
            helpPage: 'Radios',
        },
        {
            uiPageRegExp: /^\/(vmware-hosts|vmware-host-detail)$/,
            helpPage: 'Hypervisors',
        },
        {
            uiPageRegExp: /^\/(vmware-vms|vmware-vm-detail)$/,
            helpPage: 'VirtualMachines',
        },
        {
            uiPageRegExp: /^\/reports$/g,
            helpPage: 'ScheduledReports',
        },
        {
            uiPageRegExp: /^\/admin\/users/,
            helpPage: 'Users',
        },
        {
            uiPageRegExp: /^\/admin\/wifiNetworks/,
            helpPage: 'WiFiNetworks',
        },
        {
            uiPageRegExp: /^\/admin\/wiredNetworks/,
            helpPage: 'WiredNetworks',
        },
        {
            uiPageRegExp: /^\/admin\/apiTokens/,
            helpPage: 'APITokens',
        },
        {
            uiPageRegExp: /^\/admin\/sso/,
            helpPage: 'Authentication',
        },
        {
            uiPageRegExp: /^\/admin\/sites/,
            helpPage: 'Sites',
        },
        {
            uiPageRegExp: /^\/admin\/business-services/,
            helpPage: 'BusinessServices',
        },
        {
            uiPageRegExp: /^\/admin\/config/,
            helpPage: 'General',
        },
        {
            uiPageRegExp: /^\/admin\/nPoints/,
            helpPage: 'nPoints',
        },
        {
            uiPageRegExp: /^\/admin\/nPoint-settings/,
            helpPage: 'nPointSettings',
        },
        {
            uiPageRegExp: /^\/admin\/isngs/,
            helpPage: 'ISNGs',
        },
        {
            uiPageRegExp: /^\/admin\/server-health/,
            helpPage: 'ServerHealth',
        },
        {
            uiPageRegExp: /^\/admin\/auditActivities/,
            helpPage: 'AuditActivities',
        },
        {
            uiPageRegExp: /^\/admin\/active-standby-plms/,
            helpPage: 'Standby',
        },
        {
            uiPageRegExp: /^\/admin\/plms/,
            helpPage: 'LocalManagers',
        },
        {
            uiPageRegExp: /^\/admin\/maintenance-windows/,
            helpPage: 'MaintenanceWindows',
        },
        {
            uiPageRegExp: /^\/admin\/groups/,
            helpPage: 'Groups',
        },
    ],
};

function mapPathToHelpTopic(path) {
    const testPath = removePlmPrefix(path);
    const urlMapping = CONSTANTS.URL_MAPPINGS.find((mapping) => mapping.uiPageRegExp.test(testPath));
    return urlMapping?.helpPage || CONSTANTS.DEFAULT_HELP_PAGE;
}

function useHelpVersion() {
    const [version] = useVersion();
    return useMemo(() => {
        if (!version || version === 'Unknown') return CONSTANTS.FALLBACK_VERSION;
        const [major, minor] = version.split('.');
        return `v${major}${minor}`;
    }, [version]);
}

function useHelpTopic(topic) {
    const location = useLocation();
    return useMemo(() => topic || mapPathToHelpTopic(location.pathname), [topic, location]);
}

/**
 * Use the help URL for the current location or an optional topic.
 *
 * @param {String} [topic] An optional topic that will be used when constructing the URL.  If not
 * provided, the current path will be mapped to a topic.
 *
 * @returns {String} The help URL.
 */
export function useHelpUrl(topic) {
    return `${CONSTANTS.HELP_URL}/${useHelpVersion()}/${useHelpTopic(topic)}.html`;
}
